import type { SlideFields } from "@/types/Slide";
import type { PhonicsEncodeSlideData } from "@ai-teach/types";
import { HiVolumeUp } from "react-icons/hi";

interface PhonicsEncodeThumbnailProps {
  slide: SlideFields;
}

export function PhonicsEncodeThumbnail({ slide }: PhonicsEncodeThumbnailProps) {
  const data = slide.slideData as PhonicsEncodeSlideData | undefined;
  const phoneme = data?.phoneme;
  const hasAudio = !!data?.audio?.src;
  const mode = data?.questionMode ?? "multiple_choice";
  const correctAnswer = data?.correctAnswer;
  const options = data?.options ?? [];

  return (
    <div className="bg-blue-50 p-2 h-full flex flex-col gap-2 overflow-hidden">
      {/* Audio indicator */}
      <div className="flex items-center justify-center gap-2 bg-white rounded-lg py-3 border border-blue-200">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center ${
            hasAudio ? "bg-blue-500" : "bg-gray-200"
          }`}
        >
          <HiVolumeUp className={hasAudio ? "text-white text-sm" : "text-gray-400 text-sm"} />
        </div>
        {phoneme ? (
          <span className="text-2xl font-bold text-gray-800">{phoneme}</span>
        ) : (
          <span className="text-xs text-gray-400 italic">No phoneme</span>
        )}
      </div>

      {/* Question mode + answer preview */}
      <div className="flex-1 bg-white rounded-lg border border-blue-200 p-2 flex flex-col gap-1.5 overflow-hidden">
        <span className="text-[9px] font-bold text-blue-400 uppercase tracking-wider">
          {mode === "multiple_choice" ? "Multiple Choice" : "Fill in Blank"}
        </span>
        {mode === "multiple_choice" ? (
          <div className="flex flex-col gap-1">
            {correctAnswer && (
              <div className="px-2 py-0.5 rounded bg-green-100 border border-green-300 text-[10px] font-semibold text-green-700 truncate">
                ✓ {correctAnswer}
              </div>
            )}
            {options.slice(0, 2).map((opt, i) => (
              <div
                key={`thumb-opt-${i + 1}`}
                className="px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-[10px] text-gray-600 truncate"
              >
                {opt || "—"}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-2 py-1 rounded border border-dashed border-gray-300 text-[10px] text-gray-400 italic">
            {correctAnswer ? `Answer: ${correctAnswer}` : "No answer set"}
          </div>
        )}
      </div>
    </div>
  );
}
